import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Plus } from 'lucide-react';
import { api } from '../../services/api';
import { toast } from '../../store/toastStore';
import { undoableDelete } from '../../utils/undoableActions';
import { buildCloseTicketJob } from '../../utils/closeTicketJob';
import Dialog from '../ui/Dialog';
import ManualWorkLogModal from '../ManualWorkLogModal';
import { apiErrorMessage } from '../sales/shared';

export interface ClosableItem {
  id: string;
  title: string;
  task_type?: string | null;
  parent_ticket_id?: string | null;
}

interface CloseVars {
  id: string;
  title: string;
  isTask: boolean;
  cascade?: boolean;
}

/**
 * "סגירת הקריאה" from Home with the SAME guardrails as the tickets list
 * (`TicketsPage.handleStatusSelect`): warn when no time was logged (offer to log
 * it first) and when sub-tasks are still open (offer to close them too). The
 * close itself is a delayed commit with a 6 s "ביטול" (B.23): no reopen is
 * ever sent. It is a job of the P-27 scheduler (`undoableDelete`), so leaving the
 * page sends it with `keepalive` (FIX-B10).
 *
 *   const close = useCloseTicket({ onHide, onShow, onCommitted });
 *   close.request(item);  …  {close.dialogs}
 *
 * `onHide` runs when the close is scheduled, `onShow` when it is undone or
 * fails, `onCommitted` once the delayed request has succeeded.
 */
export interface UseCloseTicketOptions {
  onHide?: (id: string) => void;
  onShow?: (id: string) => void;
  /** After the delayed close request succeeded (not on undo, not on failure). */
  onCommitted?: (id: string) => void;
}

export function useCloseTicket(opts: UseCloseTicketOptions = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [noLogs, setNoLogs] = useState<CloseVars | null>(null);
  const [addLog, setAddLog] = useState<CloseVars | null>(null);
  const [subtasks, setSubtasks] = useState<{ vars: CloseVars; count: number } | null>(null);
  const [cascade, setCascade] = useState(false);
  const [checking, setChecking] = useState<string | null>(null);

  const optsRef = useRef(opts);
  optsRef.current = opts;

  // FIX-B10 (QA-P29 minor 1): the close rides the ONE P-27 scheduler, so a close
  // waiting in its 6 s window is sent with `keepalive` on pagehide / hidden tab.
  const close = useMemo(() => ({
    mutate: (v: CloseVars) => {
      optsRef.current.onHide?.(v.id);
      return undoableDelete(buildCloseTicketJob(v, {
        message: v.isTask ? t('home.close.doneTask') : t('home.close.doneTicket'),
        run: () => api.updateTicketStatus(v.id, 'completed', v.cascade),
        onCommitted: () => {
          queryClient.invalidateQueries({ queryKey: ['dashboard-daily-status'] });
          queryClient.invalidateQueries({ queryKey: ['workspace'] });
          queryClient.invalidateQueries({ queryKey: ['tickets'] });
          optsRef.current.onCommitted?.(v.id);
        },
        onRestore: () => optsRef.current.onShow?.(v.id),
        onError: (err) => toast('error', apiErrorMessage(err, t('home.close.failed'))),
      }));
    },
  }), [t, queryClient]);

  const request = useCallback(async (item: ClosableItem) => {
    const vars: CloseVars = { id: item.id, title: item.title, isTask: item.task_type === 'task' };
    setChecking(item.id);
    try {
      try {
        const wl = await api.getWorkLogs({ ticket_id: item.id, limit: 1 });
        if (!wl.data || wl.data.length === 0) {
          setNoLogs(vars);
          return;
        }
      } catch {
        /* advisory pre-check only, as on the tickets list */
      }
      if (!item.parent_ticket_id) {
        try {
          const st = await api.getSubtasks(item.id);
          const open = (st.data ?? []).filter((s: { status: string }) => s.status !== 'completed').length;
          if (open > 0) {
            setCascade(false);
            setSubtasks({ vars, count: open });
            return;
          }
        } catch {
          /* advisory pre-check only */
        }
      }
      close.mutate(vars);
    } finally {
      setChecking(null);
    }
  }, [close]);

  const dialogs = (
    <>
      <Dialog
        open={!!noLogs}
        title={t('ticketDetail.closeGuard.noLogsTitle')}
        icon={<AlertTriangle size={20} className="shrink-0 text-warning-600" aria-hidden />}
        onClose={() => setNoLogs(null)}
        size="md"
        role="alertdialog"
        footer={noLogs ? (
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            <button type="button" className="btn btn-ghost w-full sm:w-auto sm:me-auto" onClick={() => setNoLogs(null)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-secondary w-full sm:w-auto"
              onClick={() => { close.mutate(noLogs); setNoLogs(null); }}
            >
              {t('ticketDetail.closeGuard.closeAnyway')}
            </button>
            <button
              type="button"
              className="btn btn-primary w-full sm:w-auto"
              onClick={() => { setAddLog(noLogs); setNoLogs(null); }}
            >
              <Plus size={16} aria-hidden />
              {t('ticketDetail.closeGuard.addWorkLog')}
            </button>
          </div>
        ) : undefined}
      >
        <p className="text-sm text-text-2">{t('ticketDetail.closeGuard.noLogsText')}</p>
      </Dialog>

      <Dialog
        open={!!subtasks}
        title={subtasks ? t('ticketDetail.closeGuard.openSubtasks', { count: subtasks.count }) : undefined}
        icon={<AlertTriangle size={20} className="shrink-0 text-warning-600" aria-hidden />}
        onClose={() => setSubtasks(null)}
        size="sm"
        role="alertdialog"
        footer={subtasks ? (
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setSubtasks(null)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => { close.mutate({ ...subtasks.vars, cascade }); setSubtasks(null); }}
            >
              {t('ticketDetail.closeGuard.closeAnyway')}
            </button>
          </>
        ) : undefined}
      >
        {subtasks && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={cascade}
              onChange={(e) => setCascade(e.target.checked)}
              className="w-4 h-4 accent-primary-700"
            />
            <span className="text-sm text-text-2">{t('ticketDetail.closeGuard.cascade')}</span>
          </label>
        )}
      </Dialog>

      {addLog && (
        <ManualWorkLogModal
          ticketId={addLog.id}
          ticketTitle={addLog.title}
          startTime={new Date(Date.now() - 3600000).toISOString()}
          endTime={new Date().toISOString()}
          durationMinutes={60}
          onClose={() => setAddLog(null)}
          onSuccess={() => {
            const vars = addLog;
            setAddLog(null);
            queryClient.invalidateQueries({ queryKey: ['work-logs'] });
            close.mutate(vars);
          }}
        />
      )}
    </>
  );

  return { request, dialogs, checking };
}
